import React from "react";

import {
  Add as AddIcon,
  Close as CloseIcon,
  Remove as RemoveIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  IconButton,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Tooltip,
  Typography,
} from "@mui/material";
import { observer } from "mobx-react-lite";
import { makeStyles } from "tss-react/mui";

import InfoIcon from "@/components/Icons/InfoIcon";
import useKeyboardShortcut from "@/hooks/useKeyboardShortcut";
import { colors } from "@/theme/overrides/colors";
import { mealPatternNameLabel } from "@/utils/food";
import { stopPropagation } from "@/utils/functions";
import { percent, round } from "@/utils/number";

import MealPatternIcon from "./MealPatternIcon";
import MealPatternItem from "../store/MealPatternItem";

interface Props {
  item: MealPatternItem;
  onEdit: (item: MealPatternItem) => void;
}

const useStyles = makeStyles()((theme) => ({
  foodIcon: {
    color: colors.primary,
    backgroundColor: "rgba(31,212,175,0.1)", // #1FD4AF, 10% opacity
  },
  fruitIcon: {
    color: colors.secondary,
    backgroundColor: "rgba(255,157,55,0.1)", // #FF9D37, 10% opacity
  },
  infoIcon: {
    marginLeft: theme.spacing(2),
    fontSize: 16,
    color: "#C0C5CD",
  },
  addButton: {
    padding: theme.spacing(2),
    color: colors.grey,
    backgroundColor: "#E8EEF6",
  },
  textPrimary: {
    fontSize: 16,
    lineHeight: "22px",
    fontWeight: 600,
    color: colors.dark,
  },
  textSecondary: {
    display: "flex",
    alignItems: "center",
    fontSize: 14,
    lineHeight: "20px",
    fontWeight: 600,
    color: colors.grey,
  },
  epWeight: {
    background: theme.palette.grey["200"],
    borderRadius: "4px",
    padding: "2px 8px",
    marginLeft: theme.spacing(2),
  },
  controls: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    minWidth: 98,
    maxWidth: 98,
  },
  amountButton: {
    padding: theme.spacing(0.5, 2),
    border: "1px solid #C0C5CD",
    borderRadius: 0,
    width: 98,
    maxWidth: 98,
    color: colors.dark,
    "& .MuiButton-label": {
      display: "inline-block",
      width: "100%",
      overflowX: "hidden",
      whiteSpace: "nowrap",
      textOverflow: "ellipsis",
    },
  },
  warningIndicator: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    padding: "4px 8px 4px 8px",
    backgroundColor: "#FF6262",
    borderRadius: 24,
  },
  warningIndicatorText: {
    fontSize: 8,
    fontWeight: 600,
    color: colors.white,
  },
  warningLogo: {
    width: 12,
    height: 12,
  },
  tooltipArrow: {
    color: colors.dark,
  },
  tooltipTooltip: {
    backgroundColor: colors.dark,
    borderRadius: 0,
  },
  tooltipText: {
    fontSize: 12,
    lineHeight: "14px",
    fontWeight: 600,
    color: colors.white,
  },
  tooltipTextValue: {
    color: theme.palette.primary.main,
  },
  secondaryListItem: {
    display: "flex",
    alignItems: "center",
    marginTop: 4,
  },
}));

const MealPatternItemView: React.FC<Props> = observer(({ item, onEdit }) => {
  const { classes } = useStyles();

  const preventCellRipple = (e: React.MouseEvent) => {
    // Prevent buttons of this cell to trigger ripple effect of the whole cell
    e.stopPropagation();
  };

  useKeyboardShortcut("Ctrl+D", () => {
    if (item.cell.isActive) item.remove();
  });

  return (
    <ListItem disableGutters>
      <ListItemAvatar>
        <MealPatternIcon name={item.name} />
      </ListItemAvatar>
      <ListItemText
        primary={
          <>
            <Box display="flex" alignItems="center" gap="10px">
              {mealPatternNameLabel(item.name)}
            </Box>
          </>
        }
        secondary={
          <div className={classes.secondaryListItem}>
            <span>{Math.round(item.energyKcal)} kcal</span>
            <Tooltip
              arrow
              classes={{
                arrow: classes.tooltipArrow,
                tooltip: classes.tooltipTooltip,
              }}
              title={
                <div>
                  <Typography className={classes.tooltipText}>
                    Proteins :{" "}
                    <span className={classes.tooltipTextValue}>
                      {round(item.proteinGrams)}g(
                      {percent(item.proteinPercent, { round: 0 })}%)
                    </span>
                    , Fats :{" "}
                    <span className={classes.tooltipTextValue}>
                      {round(item.fatGrams)}g(
                      {percent(item.fatPercent, { round: 0 })}%)
                    </span>
                    ,<br /> Carbohydrates :{" "}
                    <span className={classes.tooltipTextValue}>
                      {round(item.carbohydrateGrams)}g(
                      {percent(item.carbohydratePercent, { round: 0 })}%)
                    </span>
                  </Typography>
                </div>
              }
            >
              <InfoIcon className={classes.infoIcon} />
            </Tooltip>
          </div>
        }
        classes={{
          primary: classes.textPrimary,
          secondary: classes.textSecondary,
        }}
      />
      <Box className={classes.controls}>
        {item.cell.isDeleting ? (
          <IconButton
            onMouseDown={preventCellRipple}
            onClick={stopPropagation(() => item.remove())}
            size="large"
          >
            <CloseIcon />
          </IconButton>
        ) : (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
          >
            <Tooltip title={item.amountWithUnit} placement="top">
              <Button
                className={classes.amountButton}
                fullWidth
                size="small"
                variant="text"
                onMouseDown={preventCellRipple}
                onClick={stopPropagation(() => onEdit(item))}
              >
                {item.amountWithUnit}
              </Button>
            </Tooltip>
            <Box
              width="100%"
              marginTop="2px"
              display="flex"
              alignItems="center"
              justifyContent="space-between"
            >
              <IconButton
                size="small"
                onMouseDown={preventCellRipple}
                onClick={stopPropagation(() => item.decrementAmount())}
              >
                <RemoveIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onMouseDown={preventCellRipple}
                onClick={stopPropagation(() => item.incrementAmount())}
              >
                <AddIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        )}
      </Box>
    </ListItem>
  );
});

export default MealPatternItemView;
